import { useReducer, useRef, useEffect } from "react"
// helper
import { cn } from "src/utils/common.helper"
import {
    CHART_INSIGHTS,
    CHART_INSIGHTS_OPTIONS,
    INITIAL_MESSAGES,
    NUMERIC_INSIGHTS,
    NUMERIC_INSIGHTS_OPTIONS,
    getAccuracies,
    getAverageTimeToLevelUp,
    getCorrectIncorrectCorelation,
    getPrecision,
} from "./helper"
import { useSelector } from "react-redux"
import { useParams } from "react-router"
import { exercisesList } from "@components/ExerciseListingPage/ExercisesList"
import animation from "@assets/ReportPage/DotsAnimation.json"
import { Player } from "@lottiefiles/react-lottie-player"

let prevContext

const messageReducer = (state, action) => {
    switch (action.type) {
        case "ADD_MESSAGE":
            return [...state, action.payload]
        default:
            return state
    }
}

export const ReportPageChatUI = () => {
    const [messages, dispatch] = useReducer(messageReducer, INITIAL_MESSAGES)
    const { profileDetails } = useSelector((state) => state.profile)
    const { gameDetails } = useParams()
    const [game, path] = gameDetails.split("@")

    const selectedExercise = profileDetails?.exercises[path]?.[game]
    const { scoreHistory } = selectedExercise ?? {}

    const exerciseName = exercisesList[path].find(
        (item) => item.code === game
    )?.name

    const exerciseLevel =
        selectedExercise?.level ?? profileDetails?.exercises[path]?.level
    const exerciseTime =
        selectedExercise?.time ?? profileDetails?.exercises[path].time

    const exerciseStartDate = new Date(
        selectedExercise?.scoreHistory[0]?.created_at
    ).toLocaleDateString()

    // Store the result of getAccuracies and getPrecision in variables
    const accuracies = getAccuracies(selectedExercise)
    const precisions = getPrecision(scoreHistory)

    const handleOptionClick = (option) => {
        dispatch({
            type: "ADD_MESSAGE",
            payload: { text: option, sender: "user" },
        })

        let newOptions
        let newText
        if (prevContext === NUMERIC_INSIGHTS) {
            newOptions = NUMERIC_INSIGHTS_OPTIONS
        } else if (prevContext === CHART_INSIGHTS) {
            newOptions = CHART_INSIGHTS_OPTIONS
        }
        switch (option) {
            case "Numeric Insights":
                newOptions = NUMERIC_INSIGHTS_OPTIONS
                prevContext = "Numeric Insights"
                break
            case "Chart Insights":
                newOptions = CHART_INSIGHTS_OPTIONS
                prevContext = "Chart Insights"
                break
            case "Level":
                newText = handleLevelOption()
                break
            case "Time":
                newText = handleTimeOption()
                break
            case "Average Accuracy":
                newText = `Your average accuracy is ${accuracies.meanAccuracy}%. Maintaining it at 90% is a positive indicator.`
                break
            case "Typical Accuracy":
                newText = `Your median accuracy is currently ${accuracies.medianAccuracy}%, and it is important to maintain and even improve this value to achieve the highest possible accuracy.`
                break
            case "Consistency":
                newText = `Your accuracy varies by ${accuracies.stdDeviationAccuracy}% from the average performance, with lower values indicating greater consistency in your performance.`
                break
            case "Correctness rate":
                newText = `Your Correct vs Incorrect report suggests that as correct attempts increase, incorrect attempts tend to ${
                    getCorrectIncorrectCorelation(scoreHistory) < 0
                        ? "decrease"
                        : "increase"
                }.`
                break
            case "Accuracy and Precision":
                newText = `Achieving 90% accuracy and a precision above 10 indicates strong performance in the exercise. For the ${exerciseName} exercise, your average accuracy is ${accuracies.meanAccuracy} and your average precision is ${precisions.averagePrecision}.`
                break
            case "Average Precision":
                newText = `Your average precision is ${precisions.averagePrecision}. Keeping it above 10 is a good sign.`
                break
            case "Typical Precision":
                {
                    const medianPrecision = precisions.medianPrecision
                    newText = `Your average precision across all levels is ${medianPrecision}, ${medianPrecision > 10 ? "exceeding the typical expected value of 10, indicates strong performance." : "you can achieve the expected value i.e 10 by consistently exercising."}`
                }
                break
            case "Precision":
                newText = `The minimum expected precision is 10. Your Precision report indicates ${precisions.averagePrecision > 10 ? "strong performance" : "it needs improvement"}
        `
                break
            case "Go Back":
                newOptions = ["Numeric Insights", "Chart Insights"]
                break
            default:
                break
        }

        setTimeout(() => {
            dispatch({
                type: "ADD_MESSAGE",
                payload: {
                    text: newText ?? `What would you like to know about?`,
                    sender: "bot",
                    options: newOptions,
                    prevContext,
                },
            })
        }, 500)
    }

    const handleLevelOption = () => {
        if (prevContext === "Numeric Insights") {
            return `You are currently at level ${exerciseLevel} and just ${6 - exerciseLevel} step away from last level.`
        } else if (prevContext === "Chart Insights") {
            return `You started this exercise on ${exerciseStartDate}. The average time to level up is ${getAverageTimeToLevelUp(scoreHistory, profileDetails, path)} days.`
        }
    }

    const handleTimeOption = () => {
        if (prevContext === "Numeric Insights") {
            return `You are currently engaged in a ${exerciseTime} minute exercise.`
        }
    }

    // Add a ref to the container div to keep it scrolled to the bottom
    const messagesEndRef = useRef(null)

    // Add useEffect to scroll to the bottom on component update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages])

    return (
        <div className="flex flex-col h-full rounded-2xl bg-white w-full overflow-hidden overflow-y-auto theme-scrollbar p-4">
            {messages.map((message, messageIndex) => (
                <div key={messageIndex}>
                    <div
                        className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"} mb-4`}
                    >
                        <div
                            className={cn("max-w-xs p-3 text-xs rounded-2xl", {
                                "bg-[var(--bg-theme-lightest)] rounded-tl-none":
                                    message.sender === "bot",
                                "bg-[var(--bg-theme-primary)] rounded-tr-none text-white":
                                    message.sender === "user",
                            })}
                        >
                            {message.text}
                        </div>
                    </div>
                    {message.options && (
                        <div className="flex gap-2 flex-wrap items-start mt-2 mb-4">
                            {message.options.map((option, optionIndex) => (
                                <div
                                    key={optionIndex}
                                    onClick={() =>
                                        messages?.length - 1 === messageIndex
                                            ? handleOptionClick(option)
                                            : null
                                    }
                                    className="py-1.5 px-3 cursor-pointer leading-4 font-semibold text-[var(--text-light-gray)] rounded-2xl text-xs max-w-xs border border-[var(--border-blue-light)] bg-[var(--bg-theme-secondary)]"
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            {/* Add a div at the end of the messages to act as a reference for scrolling */}
            <div className="ml-1 mr-auto">
                <Player
                    style={{ width: "30px" }}
                    autoplay
                    loop
                    src={animation}
                />
            </div>
            <div ref={messagesEndRef} />
        </div>
    )
}
